import { auth } from "./firebase";

const DEFAULT_STORE_WORKER_BASE_URL = "https://psh-store-worker.neelamdevisahni521.workers.dev";
const STORE_WORKER_BASE_URL = (
  process.env.REACT_APP_STORE_WORKER_BASE_URL ||
  process.env.REACT_APP_STORE_WORKER_URL ||
  DEFAULT_STORE_WORKER_BASE_URL
).replace(/\/+$/, "");

const TELEGRAM_NOTIFY_ENDPOINT = `${STORE_WORKER_BASE_URL}/api/v1/notify/telegram`;
const AUTO_APPROVE_ENDPOINT = `${STORE_WORKER_BASE_URL}/api/v1/orders/auto-approve`;
const CHECKOUT_ENDPOINT = `${STORE_WORKER_BASE_URL}/api/v1/orders/checkout`;
const QUOTE_ENDPOINT = `${STORE_WORKER_BASE_URL}/api/v1/orders/quote`;
const RATES_ENDPOINT = `${STORE_WORKER_BASE_URL}/api/v1/rates/crypto`;

export const generateIdempotencyKey = () => {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "ord_" + Date.now().toString(36) + "_" + Math.random().toString(36).substring(2, 10);
};

const getUserLabel = () => {
  const user = auth.currentUser;
  if (!user) return "Store User";
  return user.displayName || (user.email ? user.email.split("@")[0] : "Store User");
};

const getAuthToken = async () => {
  const user = auth.currentUser;
  if (!user) throw new Error("User session missing");
  return user.getIdToken();
};

const resolveOrdersUrl = () => {
  if (typeof window === "undefined" || !window.location) return "";
  try {
    return new URL("/orders", window.location.origin).toString();
  } catch {
    return "";
  }
};

const toItemsSummary = (items = []) => {
  const labels = (Array.isArray(items) ? items : [])
    .map((item) => String(item?.productName || item?.name || item?.title || "").trim())
    .filter((label) => label && !label.startsWith("-") && !/^[A-Za-z0-9]{25,36}$/.test(label));

  if (!labels.length) return "Products";
  if (labels.length <= 3) return labels.join(", ");
  return `${labels.slice(0, 3).join(", ")} +${labels.length - 3} more`;
};

/**
 * Fetch live or cached crypto rates from Cloudflare Worker.
 * Multi-source fallback (Binance -> CoinGecko -> static) eliminates client rate-limits.
 * Set { fresh: true } to guarantee an un-cached live rate direct from market before payment.
 */
export const fetchCryptoRates = async ({ fresh = false } = {}) => {
  try {
    const url = fresh ? `${RATES_ENDPOINT}?fresh=true&t=${Date.now()}` : RATES_ENDPOINT;
    const response = await fetch(url, {
      cache: fresh ? "no-store" : "default",
      headers: fresh ? { "Cache-Control": "no-cache, no-store", Pragma: "no-cache" } : {},
    });
    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.warn("[Rates] Failed to fetch crypto rates from worker:", error);
    return null;
  }
};

export const fetchLiveCryptoRates = async () => {
  return fetchCryptoRates({ fresh: true });
};

/**
 * Pre-checkout quote preview from Worker.
 * Authoritatively calculates verified prices, coupon discounts, points discount, and fees.
 */
export const requestOrderQuote = async ({
  items = [],
  paymentMethodName = "",
  couponCode = null,
  pointsToUse = 0,
} = {}) => {
  const token = await getAuthToken();
  const response = await fetch(QUOTE_ENDPOINT, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({
      items,
      paymentMethodName,
      couponCode,
      pointsToUse,
    }),
  });

  const data = await response.json().catch(() => ({}));
  if (!response.ok || data?.success === false) {
    throw new Error(data?.error || `Quote calculation failed (HTTP ${response.status})`);
  }

  return data;
};

/**
 * Authoritative Server-Side Checkout with Idempotency Protection.
 * Atomically verifies prices, validates promo code, debits reward points, creates the order,
 * auto-approves if $0, and triggers Telegram alerts in the background.
 */
export const requestOrderCheckout = async ({
  items = [],
  paymentMethod = {},
  couponCode = null,
  pointsToUse = 0,
  notes = "",
  idempotencyKey = null,
} = {}) => {
  const token = await getAuthToken();
  const activeKey = idempotencyKey || generateIdempotencyKey();

  let response;
  try {
    response = await fetch(CHECKOUT_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        items,
        paymentMethod,
        couponCode,
        pointsToUse,
        notes,
        idempotencyKey: activeKey,
      }),
    });
  } catch (networkErr) {
    console.error("[Checkout] Network or CORS fetch error:", networkErr);
    const err = new Error(
      networkErr?.message === "Failed to fetch"
        ? "Could not connect to the checkout server. Please check your network or try again."
        : (networkErr?.message || "Checkout connection error")
    );
    err.originalError = networkErr;
    throw err;
  }

  const data = await response.json().catch(() => ({}));
  if (!response.ok || data?.success === false) {
    const errorMsg = data?.error || `Checkout failed (HTTP ${response.status})`;
    const err = new Error(errorMsg);
    err.status = response.status;
    err.data = data;
    throw err;
  }

  return {
    ...data,
    idempotencyKey: activeKey,
  };
};

export const notifyOrderPendingToWorker = async ({
  orderId,
  amount = 0,
  items = [],
  customerId = "",
  customerEmail = "",
  customerName = "",
  timestamp = Date.now(),
} = {}) => {
  try {
    if (!orderId) return false;

    const token = await getAuthToken();
    const response = await fetch(TELEGRAM_NOTIFY_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        eventType: "order_pending",
        payload: {
          orderId,
          status: "pending",
          amount: Number(amount || 0),
          customerId,
          customerEmail,
          customerName,
          productSummary: toItemsSummary(items),
          ordersUrl: resolveOrdersUrl(),
          actor: getUserLabel(),
          source: "store/checkout",
          timestamp,
        },
      }),
    });

    if (!response.ok) return false;
    const data = await response.json().catch(() => null);
    return Boolean(data?.success);
  } catch (error) {
    console.warn("[Telegram Notify] Store pending order event failed:", error);
    return false;
  }
};

/**
 * Legacy zero-amount auto-approve endpoint (kept for backward compatibility).
 */
export const requestOrderAutoApproval = async ({ orderId } = {}) => {
  try {
    if (!orderId) return false;

    const token = await getAuthToken();
    const response = await fetch(AUTO_APPROVE_ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ orderId }),
    });

    if (!response.ok) return false;
    const data = await response.json().catch(() => null);
    return Boolean(data?.approved);
  } catch (error) {
    console.warn("[Auto Approve] Zero-amount approval request failed:", error);
    return false;
  }
};
